import { BrandLockup } from "./BrandLockup";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, Phone, Mail, Heart, ChevronDown, Search, UserCircle2 } from "lucide-react";
import { NAV, PORTALS } from "../lib/nav";
import { SiteSearch } from "./SiteSearch";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

const PortalMenu = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDoc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold text-[#0d1724] border border-black/10 hover:border-[#0d1724] transition-colors" data-testid="header-my-dkf">
        <UserCircle2 size={16} /> My DKF <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul role="menu" className="absolute right-0 top-full mt-2 bg-white border border-black/5 shadow-xl rounded-xl p-2 w-64 z-50" data-testid="portal-menu">
          {PORTALS.map(([label, to, hint], i) => (
            <li key={label} role="none" className={i === PORTALS.length - 1 ? "border-t border-black/5 mt-1 pt-1" : ""}>
              <Link role="menuitem" to={to} onClick={() => setOpen(false)} className="block px-3 py-2 rounded-md hover:bg-[#f8f4e8]" data-testid={`portal-${slug(label)}`}>
                <div className="text-sm font-semibold text-[#0a0d12]">{label}</div>
                <div className="text-[11px] text-[#0a0d12]/60">{hint}</div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setOpenGroup(null); }, [pathname]);

  return (
    <><a href="#main" className="skip-link" data-testid="skip-link">Skip to main content</a><span id="nav-hint" className="sr-only">Use arrow keys to move between sections, down arrow to open a section, Escape to close.</span><header id="site-header" className="sticky top-0 z-50" data-testid="site-header">
      <div className="bg-[#155e3f] text-white text-xs sm:text-sm">
        <div className="dk-container flex items-center justify-between py-2 gap-4">
          <div className="flex items-center gap-4 sm:gap-6">
            <a href="tel:+2348063550963" className="flex items-center gap-2 hover:text-[#f0ce77] transition-colors" data-testid="topbar-phone"><Phone size={14} className="text-[#d6aa43]" /> <span className="hidden sm:inline">+234 806 355 0963</span><span className="sm:hidden">Call</span></a>
            <a href="mailto:foundation@dutsenkauna.org" className="hidden md:flex items-center gap-2 hover:text-[#f0ce77] transition-colors" data-testid="topbar-email"><Mail size={14} className="text-[#d6aa43]" /> foundation@dutsenkauna.org</a>
          </div>
          <div className="text-[11px] text-white" data-testid="topbar-fraud-notice"><span className="hidden lg:inline">Applying for support is always free. </span><Link to="/complaints#fraud" className="text-[#d6aa43] hover:underline">Report fraud or impersonation</Link></div>
        </div>
      </div>

      <div className={`bg-white transition-shadow ${scrolled ? "shadow-md" : "shadow-sm"}`}>
        <div className="mx-auto max-w-[1560px] px-4 sm:px-6 flex items-center justify-between py-3 gap-4">
          <Link to="/" className="flex items-center group shrink-0" data-testid="logo-link">
            <BrandLockup tone="light" />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1" aria-label="Main" data-testid="main-nav" onKeyDown={(e) => {
            const tops = [...e.currentTarget.querySelectorAll('[data-testid^="nav-"]')];
            const idx = tops.indexOf(document.activeElement);
            if (e.key === "Escape") { e.currentTarget.querySelector('[data-testid^="nav-"]')?.focus(); document.activeElement?.blur(); }
            if (idx >= 0 && (e.key === "ArrowRight" || e.key === "ArrowLeft")) { e.preventDefault(); tops[(idx + (e.key === "ArrowRight" ? 1 : tops.length - 1)) % tops.length].focus(); }
            if (idx >= 0 && e.key === "ArrowDown") { e.preventDefault(); tops[idx].parentElement.querySelector("ul a")?.focus(); }
          }}>
            {NAV.map((n) => (
              <div key={n.label} className="relative group">
                <NavLink to={n.to} data-testid={`nav-${slug(n.label)}`} aria-haspopup="true" aria-describedby="nav-hint" className={({ isActive }) => `flex items-center gap-1 px-2 xl:px-3 py-2 text-[13px] whitespace-nowrap font-semibold tracking-wide transition-colors rounded-md ${isActive ? "text-[#d6aa43]" : "text-[#0a0d12] hover:text-[#d6aa43]"}`}>
                  {n.label} <ChevronDown size={12} className="opacity-60 group-hover:rotate-180 transition-transform" />
                </NavLink>
                <div className="absolute left-0 top-full pt-2 opacity-0 translate-y-1 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:pointer-events-auto group-hover:pointer-events-auto transition-all duration-150" data-testid={`menu-${slug(n.label)}`}>
                  <ul className="bg-white border border-black/5 shadow-xl rounded-xl p-2 min-w-[250px]" aria-label={`${n.label} pages`}>
                    {n.items.map(([label, to]) => <li key={label}><Link to={to} className="block px-3 py-2.5 min-h-[44px] rounded-md text-sm text-[#0a0d12] hover:bg-[#f8f4e8] hover:text-[#0d1724]">{label}</Link></li>)}
                  </ul>
                </div>
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button onClick={() => setSearchOpen(true)} className="p-2 rounded-md text-[#0d1724] hover:bg-[#f8f4e8]" aria-label="Search the website" title="Search (Ctrl/⌘ K)" data-testid="header-search-button"><Search size={18} /></button>
            <SiteSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
            <PortalMenu />
            <Link to="/donate" className="dk-btn-primary !py-2 !px-4 md:!px-5 text-xs md:text-sm whitespace-nowrap" data-testid="header-donate-button"><Heart size={14} /> Donate</Link>
            <button className="lg:hidden p-2 text-[#0a0d12]" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu" aria-expanded={open} data-testid="mobile-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t border-black/5 max-h-[calc(100dvh-72px)] overflow-y-auto pb-24" data-testid="mobile-menu">
            <nav className="dk-container py-3 flex flex-col">
              {NAV.map((n) => (
                <div key={n.label} className="border-b border-black/5">
                  <div className="flex items-center">
                    <NavLink to={n.to} className="flex-1 py-3 text-sm font-semibold text-[#0a0d12]" data-testid={`mobile-nav-${slug(n.label)}`}>{n.label}</NavLink>
                    <button onClick={() => setOpenGroup(openGroup === n.label ? null : n.label)} className="p-3 min-w-[44px] min-h-[44px]" aria-label={`Expand ${n.label}`} aria-expanded={openGroup === n.label} data-testid={`mobile-expand-${slug(n.label)}`}><ChevronDown size={16} className={`transition-transform ${openGroup === n.label ? "rotate-180" : ""}`} /></button>
                  </div>
                  {openGroup === n.label && <ul className="pb-3 pl-3">{n.items.map(([label, to]) => <li key={label}><Link to={to} className="block py-2.5 text-sm text-[#0a0d12]/80">{label}</Link></li>)}</ul>}
                </div>
              ))}
              <div className="pt-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0d1724]/60 font-semibold mb-2 flex items-center gap-2"><UserCircle2 size={14} /> My DKF / Login</div>
                <ul className="grid grid-cols-2 gap-2" data-testid="mobile-portal-menu">
                  {PORTALS.map(([label, to]) => <li key={label}><Link to={to} className="block px-3 py-2.5 rounded-md border border-black/10 text-sm font-semibold text-[#0a0d12] text-center" data-testid={`mobile-portal-${slug(label)}`}>{label}</Link></li>)}
                </ul>
              </div>
              <Link to="/apply" className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] justify-center mt-4" data-testid="mobile-apply-button">Apply for Support — it's free</Link>
            </nav>
          </div>
        )}
      </div>
    </header></>
  );
}
